import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    alpha,
    AppBar,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    InputAdornment,
    Skeleton,
    Stack,
    TextField,
    Toolbar,
    Typography,
} from "@mui/material";
import AppLogo from "../../components/AppLogo";
import SearchIcon from "@mui/icons-material/Search";
import { fetchPublicSchools } from "../../redux/schoolPageRelated/schoolPageHandle";
import { goToPublicSchool } from "../../utils/schoolSlug";
import { APP_NAME, APP_TAGLINE } from "../../config/brand";

const SchoolDirectory = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { publicSchools, loading } = useSelector((s) => s.schoolPage);
    const [search, setSearch] = useState("");

    useEffect(() => {
        dispatch(fetchPublicSchools());
    }, [dispatch]);

    const schoolCards = useMemo(() => {
        const list = Array.isArray(publicSchools) ? publicSchools : [];
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter((school) => {
            const name = String(school.schoolName || "").toLowerCase();
            const district = String(school.schoolDistrict || "").toLowerCase();
            return name.includes(q) || district.includes(q);
        });
    }, [publicSchools, search]);

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
            <AppBar
                position="sticky"
                color="inherit"
                elevation={0}
                sx={{ borderBottom: 1, borderColor: "divider" }}
            >
                <Toolbar>
                    <AppLogo size={28} sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
                        {APP_NAME}
                    </Typography>
                    <Button variant="contained" size="small" onClick={() => navigate("/Adminregister")}>
                        Register your school
                    </Button>
                </Toolbar>
            </AppBar>

            <Box sx={{ py: 8, bgcolor: "grey.50", borderBottom: 1, borderColor: "divider" }}>
                <Container maxWidth="md" sx={{ textAlign: "center" }}>
                    <Typography variant="h3" fontWeight={800} gutterBottom>
                        Find your school
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        Browse schools using {APP_NAME} across Malawi
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="Search by school name or district..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ maxWidth: 480, mx: "auto", display: "block" }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 6 }}>
                {loading ? (
                    <Grid container spacing={3}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2 }} />
                            </Grid>
                        ))}
                    </Grid>
                ) : schoolCards.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" sx={{ py: 8 }}>
                        No schools found
                    </Typography>
                ) : (
                    <Grid container spacing={3}>
                        {schoolCards.map((school) => {
                            const schoolId = school.schoolId || school._id;
                            const accent = school.accentColor || "#1a535c";
                            const initial = (school.schoolName || "S").charAt(0).toUpperCase();
                            const locationLine = [school.schoolDistrict, school.schoolRegion ? `${school.schoolRegion} Region` : ""]
                                .filter(Boolean)
                                .join(" · ");

                            return (
                                <Grid item xs={12} sm={6} md={4} key={schoolId}>
                                    <Card
                                        elevation={0}
                                        variant="outlined"
                                        onClick={() => goToPublicSchool(school, navigate)}
                                        sx={{
                                            cursor: "pointer",
                                            transition: "all .2s",
                                            "&:hover": {
                                                transform: "translateY(-3px)",
                                                boxShadow: "0 12px 32px rgba(0,0,0,0.1)",
                                                borderColor: "primary.main",
                                            },
                                        }}
                                    >
                                        <CardContent>
                                            <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                                                <Avatar
                                                    sx={{
                                                        bgcolor: alpha(accent, 0.12),
                                                        color: accent,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {initial}
                                                </Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                                                        {school.schoolName}
                                                    </Typography>
                                                    {locationLine ? (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {locationLine}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            </Stack>

                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                {school.schoolType ? (
                                                    <Chip label={school.schoolType} size="small" />
                                                ) : null}
                                                {school.schoolOwnership ? (
                                                    <Chip label={school.schoolOwnership} size="small" variant="outlined" />
                                                ) : null}
                                                {school.boardingModeLabel ? (
                                                    <Chip label={school.boardingModeLabel} size="small" color="info" />
                                                ) : school.hasBoarding ? (
                                                    <Chip label="Boarding" size="small" color="info" />
                                                ) : null}
                                            </Stack>

                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                sx={{ mt: 1.5 }}
                                            >
                                                <Typography variant="caption" color="text.secondary">
                                                    {school.studentCount ?? 0} students
                                                </Typography>
                                                <Typography variant="caption" color="primary" fontWeight={600}>
                                                    View page →
                                                </Typography>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Container>

            <Typography variant="caption" textAlign="center" color="text.secondary" sx={{ display: "block", py: 3 }}>
                Powered by {APP_NAME} · {APP_TAGLINE}
            </Typography>
        </Box>
    );
};

export default SchoolDirectory;
